import { useState } from 'react'
import { Button, cn, useKumoToastManager } from '@cloudflare/kumo'
import { ExportIcon } from '@phosphor-icons/react'
import type { GuestInvitationDto } from '@hub-wifi/shared'
import { useTranslation } from '../../i18n'
import { guestContact, initials } from './agenda'
import { shareLink } from './share'

export interface GuestRowProps {
  invitation: GuestInvitationDto
  /** `nested`: under a booking (its time window is the booking's).
   *  `standalone`: own agenda row with avatar and time window. */
  variant: 'nested' | 'standalone'
}

/** Name-first guest row (contact muted underneath) with a 44px Share button. */
export function GuestRow({ invitation, variant }: GuestRowProps) {
  const { t } = useTranslation()
  const toasts = useKumoToastManager()
  const [busy, setBusy] = useState(false)
  const contact = guestContact(invitation)
  const timeWindow = `${invitation.visitTimeBegin} – ${invitation.visitTimeEnd}`

  async function onShare() {
    setBusy(true)
    try {
      const outcome = await shareLink(invitation.invitationUrl)
      if (outcome === 'copied') {
        toasts.add({ variant: 'success', title: t('dashboard.agenda.linkCopied') })
      } else if (outcome === 'failed') {
        toasts.add({ variant: 'error', title: t('dashboard.agenda.shareFailed') })
      }
    } finally {
      setBusy(false)
    }
  }

  // Nested guests share their booking's time window (the server copies it),
  // so only standalone rows repeat it.
  const meta = [
    variant === 'standalone' ? t('dashboard.agenda.guestMeta', { time: timeWindow }) : null,
    invitation.singleAccess ? t('dashboard.agenda.singleEntry') : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <li className={cn('home-row', variant === 'nested' && 'home-row--nested')}>
      {variant === 'standalone' && (
        <span className="home-avatar" aria-hidden>
          {initials(invitation.guestName)}
        </span>
      )}
      <div className="home-row__text">
        <span className="home-row__title">{invitation.guestName}</span>
        {meta && <span className="home-row__meta">{meta}</span>}
        {contact && <span className="home-row__meta">{contact}</span>}
      </div>
      <Button
        variant="ghost"
        shape="square"
        className="home-icon-btn"
        icon={<ExportIcon size={20} />}
        aria-label={t('dashboard.agenda.share', { name: invitation.guestName })}
        disabled={busy}
        onClick={() => void onShare()}
      />
    </li>
  )
}
